import { Container } from 'typedi';
import { SessionTimerInterval, UserContextToken, WindowToken } from '../../injection-tokens';
import { PlatformLogoutReason } from '../../models/enums/general-enums';
import {
    EventFormatterBuilder,
    EventFormatterBuilderFactory,
} from '@unified-client/event-formatter';
import {
    IIdentityChannel,
    ISessionChannel,
    MessageBroker,
    IHeartBeat,
    ILogger,
} from '@sparkware/uc-sdk-core';
import { LoggerProvider } from '../logger';
import { AuthenticationFlowType } from '../authentication/enums';
import { ClickStreamTrackingProvider } from '../tracking';
import SessionApi from '../../../APIs/SessionApi';
import { IUserContext } from '../user-context/user-context-interface';
import { Utils } from '../utils';
import { IMrGreenMigration } from '../mr-green-migration/models/interfaces/IMrGreenMigration';
import { IAuthenticationFlowFactory, IAuthenticationFlowRunner } from '../authentication/models';
import { ISessionUserData } from './interfaces/ISessionUserData';
import { IGeolocation } from '../geolocation/models/IGeolocation';
import { ISessionManager } from './interfaces/ISessionManager';
import { LoaderManager } from '../../loaders/LoaderManager';
import { ILogout } from '../logout/models';
import { RealityCheck } from '../reality-check/reality.check';
import { ILoyaltyIndication } from '../loyalty-indication/models/ILoyaltyIndication';
import { IPushController } from '../push/models/IPushController';
import { UrlUtils } from '../utils/urlUtils';
import { UserareaService } from '../external/userarea';

export abstract class BaseSessionManager implements ISessionManager {
    private _timerId: number;
    private _lastInteractionTime: number;
    private _crossLoginFlow?: IAuthenticationFlowRunner;
    private readonly _realityCheck: RealityCheck;
    private readonly _channel: ISessionChannel;
    private readonly _clickStreamTrackingProvider: ClickStreamTrackingProvider;
    private readonly _eventFormatterBuilder: EventFormatterBuilder;
    private readonly _identityChannel: IIdentityChannel;
    private readonly _logger: ILogger;
    private readonly _messagePublisher: string = 'UCF.SDK.SessionManager';
    private readonly _timerIntervalInMilliseconds: number;
    private readonly _userContext: IUserContext;
    private readonly _window: Window;
    private readonly _urlUtils: UrlUtils;
    private readonly _utils: Utils;
    private readonly _userAreaService: UserareaService;

    private get _geolocationPromise(): Promise<IGeolocation> {
        return LoaderManager.Instance.GeolocationLoader.Instance;
    }

    get timerId() {
        return this._timerId;
    }

    private get _mrGreenMigrationPromise(): Promise<IMrGreenMigration> {
        return LoaderManager.Instance.MrGreenMigrationLoader.Instance;
    }

    private get _authenticationFlowFactoryPromise(): Promise<IAuthenticationFlowFactory> {
        return LoaderManager.Instance.AuthenticationFlowFactoryLoader.Instance;
    }

    private get _logoutPromise(): Promise<ILogout> {
        return LoaderManager.Instance.LogoutLoader.Instance;
    }

    private get _loyaltyIndicationPromise(): Promise<ILoyaltyIndication> {
        return LoaderManager.Instance.LoyaltyIndicationLoader.Instance;
    }

    private get _pushPromise(): Promise<IPushController> {
        return LoaderManager.Instance.PushLoader.Instance;
    }

    private get isBossMode(): boolean {
        return this._utils.isBossMode();
    }

    protected constructor() {
        this._logger = Container.get(LoggerProvider).getLogger('SessionManager');
        this._window = Container.get(WindowToken);
        this._userContext = Container.get(UserContextToken);
        this._urlUtils = Container.get(UrlUtils);
        this._utils = Container.get(Utils);
        const eventFormatterBuilderFactory = Container.get(EventFormatterBuilderFactory);
        this._eventFormatterBuilder =
            eventFormatterBuilderFactory.createEventFormatterBuilder('SessionManager');
        this._channel = MessageBroker.getInstance().session;
        this._timerIntervalInMilliseconds = Container.get(SessionTimerInterval);
        this._identityChannel = MessageBroker.getInstance().identity;
        this._clickStreamTrackingProvider = Container.get(ClickStreamTrackingProvider);
        this._realityCheck = Container.get(RealityCheck);
        this._clickStreamTrackingProvider = Container.get(ClickStreamTrackingProvider);
        this._userAreaService = Container.get(UserareaService);
    }

    public onUserInteraction = (): void => {
        if (this._userContext.IsAuthenticated) {
            this._lastInteractionTime = Date.now();
        }
    };

    public onLogoutMessage = async () => {
        this._clearTimerId();
    };

    public onInactivityTimeoutMessage = async () => {
        this._sendClientStartedLogoutProcessEvent();
        this._clearTimerId();
        if (this._window.navigator.onLine === true) {
            await (
                await this._logoutPromise
            )?.doLogoutAndShowTimeoutDialog(PlatformLogoutReason.ClientDisconnected);
        }
    };

    public onHeartbeatMessage = (data?: any) => {
        if (!!data?.updateUserInteraction) {
            this.onUserInteraction();
        }
    };

    /**
     * Refreshes the session user data
     * notifies the user-area and the offering client about the refresh.
     * @param data
     */
    public refreshSessionUserData = async (data: ISessionUserData) => {
        const mrGreenMigration = await this._mrGreenMigrationPromise;
        if (mrGreenMigration) {
            await mrGreenMigration.SendBiVisitEvent();
        }
        this._userContext.refresh();
        if (this._userContext.IsAuthenticated) {
            const geolocation = await this._geolocationPromise;
            await geolocation?.initFlowAfterRefresh();
            await this.onUserAuthenticationSucceeded();
            void this._userAreaService.executeOnload(() => {
                this._identityChannel.topics.loginSuccess.publish(
                    { publisher: this._messagePublisher },
                    {
                        InitialState: data.StoreState,
                        AuthenticateData: data.AuthenticateData,
                    },
                );
            });
            void this._realityCheck.realityCheck();
        }
    };

    public onUserAuthenticationSucceeded = async (): Promise<void> => {
        await this._registerPush();
        await this._clientLoginSuccessBossMode();
    };

    protected _tryAutoLogin = async () => {
        this._logger.debug(`[_tryAutoLogin] started`);
        const authenticationFlowFactory = await this._authenticationFlowFactoryPromise;
        this._crossLoginFlow = await authenticationFlowFactory.create(AuthenticationFlowType.Cross);
        await this._crossLoginFlow.execute();
        this._logger.debug(`[_tryAutoLogin] end`);
    };

    protected _setTimerId = () => {
        this._timerId = this._window.setInterval(
            this._onInterval,
            this._timerIntervalInMilliseconds,
        );
    };

    private _sendClientStartedLogoutProcessEvent = () => {
        const formatter = this._eventFormatterBuilder.createFormatter(
            '_sendClientStartedLogoutProcessEvent',
        );
        const elasticEventObj = formatter.formatUCEvent(
            { message: 'Customer logout request by the offering client.' },
            { correlationID: this._utils.generateCorrelationID() },
            { event: 'Client logout request' },
        );

        this._clickStreamTrackingProvider.sendEventV2(elasticEventObj);
    };

    private _clearTimerId = () => {
        if (this.timerId) clearInterval(this.timerId);
    };

    private _onInterval = () => {
        let timePassedSinceLastInteraction = Date.now() - this._lastInteractionTime;
        this._logger.debug(
            `onInterval. timePassedSinceLastInteraction: ${
                timePassedSinceLastInteraction / 1000
            } seconds`,
        );
        if (timePassedSinceLastInteraction <= this._timerIntervalInMilliseconds) {
            this._sendHeartBeat();
        }
    };

    private _sendHeartBeat = (): void => {
        this._logger.debug(
            `sendHeartBeat. Sending heartbeat to Session Management System at : ${new Date().toLocaleString()}`,
        );
        SessionApi.HeartBeat();
        this._channel.topics.heartBeat.publish(
            {
                publisher: 'ucf.sdk',
            },
            {} as IHeartBeat,
        );
    };

    private async _registerPush(): Promise<void> {
        try {
            this._logger.debug(`[_registerPush] started`);
            const push = await this._pushPromise;
            if (push.isTriple8Registered) {
                this._logger.debug(
                    `[_registerPush] triple8 already registered, skipping registration`,
                );
                return;
            }
            const isClientMode = this._urlUtils.isClientMode();
            if (!this.isBossMode && !isClientMode) {
                const triple8Subscription = {
                    name: 'Triple8',
                    onSubscribed: (data) => {
                        push.onSubscribeTriple8(data);
                    },
                    onSubscribedFailed: push?.onFailTriple8Subscribe,
                };
                const loyaltyIndication = await this._loyaltyIndicationPromise;
                const loyaltyIndicationSubscription = {
                    name: 'LoyaltyIndication',
                    onSubscribed: (data) => {
                        loyaltyIndication?.init(data);
                    },
                };
                push.addPushSubscriber(triple8Subscription);
                push.addPushSubscriber(loyaltyIndicationSubscription);
                push.registerPushUpdates();
            }
            this._logger.debug(`[_registerPush] end`);
        } catch (err) {
            this._logger.error(`[_registerPush] Failed: '${JSON.stringify(err)}'`);
        }
    }

    private async _clientLoginSuccessBossMode(): Promise<void> {
        if (!this.isBossMode) return;
        const logout = await this._logoutPromise;
        await logout?.doLogoutWithoutRefresh(null, false);
    }
}
